import React, { useEffect, useState, useMemo, forwardRef } from "react";
import axios from "axios";

// Material Dashboard components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DataTable from "examples/Tables/DataTable";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import BASE_URL from "BASE_URL";

import {
  Card,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  CircularProgress,
  Snackbar,
  Alert,
  Chip,
  Tooltip,
  Box,
  Stack,
  TextField,
  Switch,
  FormControlLabel,
  Slide,
  Grow,
  Zoom,
  Fade,
  Paper,
  LinearProgress,
} from "@mui/material";
import { keyframes } from "@mui/system";

import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import SaveIcon from "@mui/icons-material/Save";
import UpdateIcon from "@mui/icons-material/Update";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import FilterListIcon from "@mui/icons-material/FilterList";
import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ExtensionIcon from "@mui/icons-material/Extension";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import PostAddIcon from "@mui/icons-material/PostAdd";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import StarIcon from "@mui/icons-material/Star";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";

// Premium Animations
const slideUp = keyframes`
  from { transform: translateY(100px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
`;

const pulseRing = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(156, 39, 176, 0.4); }
  70% { box-shadow: 0 0 0 15px rgba(156, 39, 176, 0); }
  100% { box-shadow: 0 0 0 0 rgba(156, 39, 176, 0); }
`;

const glow = keyframes`
  0% { box-shadow: 0 0 5px rgba(156, 39, 176, 0.2); }
  50% { box-shadow: 0 0 25px rgba(156, 39, 176, 0.6); }
  100% { box-shadow: 0 0 5px rgba(156, 39, 176, 0.2); }
`;

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const bounceIn = keyframes`
  0% { transform: scale(0); opacity: 0; }
  50% { transform: scale(1.2); }
  70% { transform: scale(0.9); }
  100% { transform: scale(1); opacity: 1; }
`;

const shakeAnimation = keyframes`
  0%, 100% { transform: translateX(0); }
  10%, 30%, 50%, 70%, 90% { transform: translateX(-2px); }
  20%, 40%, 60%, 80% { transform: translateX(2px); }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-5px); }
`;

const rotateIn = keyframes`
  from { transform: rotate(-180deg) scale(0); opacity: 0; }
  to { transform: rotate(0deg) scale(1); opacity: 1; }
`;

// Transition component
const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} timeout={500} />;
});

function ExtraPlanManagement() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openForm, setOpenForm] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deletePlanName, setDeletePlanName] = useState("");
  const [formMode, setFormMode] = useState("add");
  const [selectedId, setSelectedId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  // Filter states
  const [filterName, setFilterName] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [showFilters, setShowFilters] = useState(false);

  // Form states
  const [formData, setFormData] = useState({
    name: "",
    post_count: "",
    view_count: "",
    price: "",
    sort_order: "",
    status: "active",
  });

  const token = localStorage.getItem("token");
  const metaPurple = "#9C27B0";

  // Fetch all extra plans
  const fetchPlans = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}/extra-plan/get`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.success) {
        setPlans(response.data.data);
      } else {
        showSnackbar(response.data.message || "Failed to fetch extra plans", "error");
      }
    } catch (error) {
      console.error("Error fetching extra plans:", error);
      showSnackbar(error.response?.data?.message || "Error fetching extra plans", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlans();
  }, []);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle form dialog open
  const handleOpenDialog = (mode = "add", item = null) => {
    setFormMode(mode);
    if (mode === "edit" && item) {
      setSelectedId(item._id);
      setFormData({
        name: item.name || "",
        post_count: item.post_count || "",
        view_count: item.view_count || "",
        price: item.price || "",
        sort_order: item.sort_order || "",
        status: item.status || "active",
      });
    } else {
      resetForm();
    }
    setOpenForm(true);
  };

  // Handle view dialog open
  const handleViewDialog = (plan) => {
    setSelectedPlan(plan);
    setViewDialogOpen(true);
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      name: "",
      post_count: "",
      view_count: "",
      price: "",
      sort_order: "",
      status: "active",
    });
    setSelectedId(null);
  };

  // Handle form submit
  const handleSubmit = async () => {
    // Validation
    if (!formData.name.trim()) {
      showSnackbar("Plan name is required", "warning");
      return;
    }
    if (!formData.post_count) {
      showSnackbar("Post count is required", "warning");
      return;
    }
    if (!formData.view_count) {
      showSnackbar("View count is required", "warning");
      return;
    }
    if (!formData.price) {
      showSnackbar("Price is required", "warning");
      return;
    }
    if (!formData.sort_order) {
      showSnackbar("Sort order is required", "warning");
      return;
    }

    setIsSubmitting(true);
    try {
      let response;
      const payload = {
        name: formData.name,
        post_count: parseInt(formData.post_count),
        view_count: parseInt(formData.view_count),
        price: parseFloat(formData.price),
        sort_order: parseInt(formData.sort_order),
        status: formData.status,
      };

      if (formMode === "add") {
        response = await axios.post(`${BASE_URL}/extra-plan/add`, payload, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });
      } else {
        response = await axios.put(`${BASE_URL}/extra-plan/update?id=${selectedId}`, payload, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });
      }

      if (response.data.success) {
        showSnackbar(
          `Extra plan ${formMode === "add" ? "added" : "updated"} successfully!`,
          "success"
        );
        fetchPlans();
        setOpenForm(false);
        resetForm();
      } else {
        showSnackbar(response.data.message || "Operation failed", "error");
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      showSnackbar(error.response?.data?.message || "Operation failed", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle delete dialog open
  const handleOpenDeleteDialog = (id, name) => {
    setDeleteId(id);
    setDeletePlanName(name);
    setDeleteDialogOpen(true);
  };

  // Handle delete plan
  const handleDeletePlan = async () => {
    setIsSubmitting(true);
    try {
      const response = await axios.delete(`${BASE_URL}/extra-plan/delete?id=${deleteId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.success) {
        showSnackbar("Extra plan deleted successfully!", "success");
        fetchPlans();
        setDeleteDialogOpen(false);
      } else {
        showSnackbar(response.data.message || "Delete failed", "error");
      }
    } catch (error) {
      console.error("Error deleting extra plan:", error);
      showSnackbar(error.response?.data?.message || "Failed to delete extra plan", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  // Clear filters
  const clearFilters = () => {
    setFilterName("");
    setFilterStatus("");
  };

  // Filtered plans
  const filteredPlans = useMemo(() => {
    let filtered = [...plans];

    if (filterName) {
      filtered = filtered.filter((item) =>
        item.name.toLowerCase().includes(filterName.toLowerCase())
      );
    }

    if (filterStatus) {
      filtered = filtered.filter((item) => item.status === filterStatus);
    }

    return filtered;
  }, [plans, filterName, filterStatus]);

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Format price
  const formatPrice = (price) => {
    return `₹${price}`;
  };

  // Get status chip
  const getStatusChip = (status) => {
    return status === "active" ? (
      <Chip
        label="ACTIVE"
        size="small"
        sx={{ bgcolor: "#e8f5e9", color: "#2e7d32", fontWeight: "bold" }}
      />
    ) : (
      <Chip
        label="INACTIVE"
        size="small"
        sx={{ bgcolor: "#ffebee", color: "#d32f2f", fontWeight: "bold" }}
      />
    );
  };

  const columns = [
    // {
    //   Header: (
    //     <MDTypography variant="caption" fontWeight="bold">
    //       S.No
    //     </MDTypography>
    //   ),
    //   accessor: "sno",
    //   align: "center",
    //   width: "5%",
    // },
    {
      Header: (
        <MDTypography variant="caption" fontWeight="bold">
          PLAN NAME
        </MDTypography>
      ),
      accessor: "name",
      align: "left",
      width: "25%",
    },
    {
      Header: (
        <MDTypography variant="caption" fontWeight="bold">
          POSTS
        </MDTypography>
      ),
      accessor: "post_count",
      align: "center",
      width: "15%",
    },
    {
      Header: (
        <MDTypography variant="caption" fontWeight="bold">
          VIEWS
        </MDTypography>
      ),
      accessor: "view_count",
      align: "center",
      width: "15%",
    },
    {
      Header: (
        <MDTypography variant="caption" fontWeight="bold">
          PRICE
        </MDTypography>
      ),
      accessor: "price",
      align: "center",
      width: "15%",
    },
    {
      Header: (
        <MDTypography variant="caption" fontWeight="bold">
          SORT ORDER
        </MDTypography>
      ),
      accessor: "sort_order",
      align: "center",
      width: "10%",
    },
    {
      Header: (
        <MDTypography variant="caption" fontWeight="bold">
          STATUS
        </MDTypography>
      ),
      accessor: "status",
      align: "center",
      width: "10%",
    },
    // {
    //   Header: (
    //     <MDTypography variant="caption" fontWeight="bold">
    //       CREATED
    //     </MDTypography>
    //   ),
    //   accessor: "createdAt",
    //   align: "center",
    //   width: "12%",
    // },
    {
      Header: (
        <MDTypography variant="caption" fontWeight="bold">
          ACTIONS
        </MDTypography>
      ),
      accessor: "action",
      align: "center",
      width: "8%",
    },
  ];

  const rows = useMemo(
    () =>
      filteredPlans.map((plan, index) => ({
        sno: (
          <MDTypography variant="caption" fontWeight="medium" textAlign="center">
            {index + 1}
          </MDTypography>
        ),
        name: (
          <MDBox>
            <MDTypography variant="button" fontWeight="bold" color="dark">
              {plan.name}
            </MDTypography>
          </MDBox>
        ),
        post_count: (
          <MDBox display="flex" alignItems="center" justifyContent="center" gap={1}>
            <PostAddIcon sx={{ color: "#d81414ff", fontSize: 18 }} />
            <MDTypography variant="h6" fontWeight="bold" color="primary">
              {plan.post_count}
            </MDTypography>
          </MDBox>
        ),
        view_count: (
          <MDBox display="flex" alignItems="center" justifyContent="center" gap={1}>
            <VisibilityIcon sx={{ color: "#d81414ff", fontSize: 18 }} />
            <MDTypography variant="h6" fontWeight="bold" color="primary">
              {plan.view_count || "0"}
            </MDTypography>
          </MDBox>
        ),
        price: (
          <MDTypography variant="h6" fontWeight="bold" color="error">
            {formatPrice(plan.price)}
          </MDTypography>
        ),
        sort_order: (
          <MDTypography variant="button" fontWeight="medium" textAlign="center">
            {plan.sort_order}
          </MDTypography>
        ),
        status: <MDBox textAlign="center">{getStatusChip(plan.status)}</MDBox>,
        createdAt: (
          <MDTypography variant="caption" fontWeight="medium" textAlign="center">
            {formatDate(plan.createdAt)}
          </MDTypography>
        ),
        action: (
          <MDBox display="flex" justifyContent="center" gap={0.5}>
            <Tooltip title="View Details">
              <IconButton
                color="default"
                onClick={() => handleViewDialog(plan)}
                size="small"
                sx={{ transition: "all 0.3s", "&:hover": { transform: "scale(1.1)" } }}
              >
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Edit Plan">
              <IconButton
                color="info"
                onClick={() => handleOpenDialog("edit", plan)}
                size="small"
                sx={{ transition: "all 0.3s", "&:hover": { transform: "scale(1.1)" } }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete Plan">
              <IconButton
                color="error"
                onClick={() => handleOpenDeleteDialog(plan._id, plan.name)}
                size="small"
                sx={{ transition: "all 0.3s", "&:hover": { transform: "scale(1.1)" } }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </MDBox>
        ),
      })),
    [filteredPlans]
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card sx={{ borderRadius: "24px" }}>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="dark"
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                flexWrap="wrap"
                gap={2}
              >
                <MDBox display="flex" alignItems="center" gap={1}>
                  <ExtensionIcon sx={{ color: "#fff", fontSize: 28 }} />
                  <MDTypography variant="h6" color="white">
                    Extra Plan Management
                  </MDTypography>
                </MDBox>
                <MDBox display="flex" gap={1}>
                  {/* <MDButton
                    variant="gradient"
                    color="dark"
                    startIcon={<FilterListIcon />}
                    onClick={() => setShowFilters(!showFilters)}
                    sx={{ borderRadius: "12px", textTransform: "none" }}
                  >
                    Filters
                  </MDButton> */}
                  <MDButton
                    variant="gradient"
                    color="dark"
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenDialog("add")}
                  >
                    Add Extra Plan
                  </MDButton>
                </MDBox>
              </MDBox>

              {/* Filters Section */}
              {showFilters && (
                <Fade in={showFilters}>
                  <Box sx={{ pt: 3, pl: 3 }}>
                    <Grid container spacing={2} alignItems="flex-end">
                      <Grid item xs={12} md={3}>
                        <TextField
                          fullWidth
                          label="Search by Plan Name"
                          value={filterName}
                          onChange={(e) => setFilterName(e.target.value)}
                          placeholder="Enter plan name..."
                          variant="outlined"
                          size="small"
                          InputProps={{
                            startAdornment: <SearchIcon sx={{ color: "#9e9e9e", mr: 1 }} />,
                          }}
                          sx={{
                            "& .MuiOutlinedInput-root": { borderRadius: "10px" },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <TextField
                          select
                          fullWidth
                          value={filterStatus}
                          onChange={(e) => setFilterStatus(e.target.value)}
                          variant="outlined"
                          size="small"
                          SelectProps={{ native: true }}
                          sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
                        >
                          <option value="">All Status</option>
                          <option value="active">Active</option>
                          <option value="inactive">Inactive</option>
                        </TextField>
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <MDButton
                          variant="gradient"
                          color="info"
                          onClick={clearFilters}
                          startIcon={<ClearIcon />}
                          fullWidth
                          sx={{ borderRadius: "10px", height: "40px" }}
                        >
                          Clear
                        </MDButton>
                      </Grid>
                    </Grid>
                  </Box>
                </Fade>
              )}

              <MDBox pt={3}>
                {loading ? (
                  <MDBox display="flex" justifyContent="center" py={5}>
                    <CircularProgress sx={{ color: metaPurple }} />
                  </MDBox>
                ) : filteredPlans.length === 0 ? (
                  <MDBox display="flex" justifyContent="center" py={5}>
                    <MDTypography variant="h6" color="dark" fontWeight="medium">
                      No extra plans found. Add your first extra plan.
                    </MDTypography>
                  </MDBox>
                ) : (
                  <DataTable
                    table={{ columns, rows }}
                    isSorted={false}
                    entriesPerPage={false}
                    showTotalEntries={true}
                    noEndBorder
                  />
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {/* PREMIUM ADD/EDIT DIALOG */}
      <Dialog
        open={openForm}
        onClose={() => !isSubmitting && setOpenForm(false)}
        fullWidth
        maxWidth="sm"
        TransitionComponent={Transition}
        PaperProps={{
          sx: {
            borderRadius: "28px",
            background: "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
            boxShadow: "0 40px 80px rgba(0, 0, 0, 0.2)",
            position: "relative",
            overflow: "hidden",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: "4px",
              background: `linear-gradient(90deg, ${metaPurple}, #CE93D8, ${metaPurple})`,
              animation: `${shimmer} 2s infinite linear`,
              backgroundSize: "200% 100%",
            },
          },
        }}
      >
        <IconButton
          onClick={() => !isSubmitting && setOpenForm(false)}
          sx={{
            position: "absolute",
            right: 16,
            top: 16,
            zIndex: 10,
            bgcolor: "rgba(0,0,0,0.05)",
            transition: "all 0.3s ease",
            "&:hover": {
              transform: "rotate(90deg) scale(1.1)",
              bgcolor: `rgba(156, 39, 176, 0.1)`,
            },
          }}
          disabled={isSubmitting}
        >
          <CloseIcon />
        </IconButton>

        <Box
          sx={{
            position: "absolute",
            top: -50,
            left: -50,
            right: -50,
            bottom: -50,
            background: `radial-gradient(circle, rgba(156,39,176,0.08) 0%, rgba(156,39,176,0) 70%)`,
            pointerEvents: "none",
            zIndex: 0,
          }}
        />

        <DialogTitle sx={{ pb: 1, pt: 3, position: "relative", zIndex: 1 }}>
          <Grow in={openForm} timeout={600}>
            <MDBox display="flex" alignItems="center" gap={2}>
              <Zoom in={openForm} timeout={600}>
                <MDBox
                  sx={{
                    background: `linear-gradient(135deg, ${metaPurple} 0%, #CE93D8 100%)`,
                    width: 56,
                    height: 56,
                    borderRadius: "18px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: `0 8px 20px rgba(156, 39, 176, 0.3)`,
                    animation: `${glow} 2s ease-in-out infinite`,
                  }}
                >
                  {formMode === "add" ? (
                    <AddIcon sx={{ fontSize: 32, color: "white" }} />
                  ) : (
                    <UpdateIcon sx={{ fontSize: 32, color: "white" }} />
                  )}
                </MDBox>
              </Zoom>

              <MDBox>
                <MDTypography
                  variant="h4"
                  fontWeight="bold"
                  sx={{
                    background: `linear-gradient(135deg, ${metaPurple} 0%, #7B1FA2 100%)`,
                    backgroundClip: "text",
                    WebkitBackgroundClip: "text",
                    color: "transparent",
                  }}
                >
                  {formMode === "add" ? "Add Extra Plan" : "Update Extra Plan"}
                </MDTypography>
                <MDTypography variant="body2" color="textSecondary">
                  {formMode === "add"
                    ? "Create a new extra post pack"
                    : "Edit existing extra plan details"}
                </MDTypography>
              </MDBox>
            </MDBox>
          </Grow>
        </DialogTitle>

        <DialogContent sx={{ position: "relative", zIndex: 1 }}>
          <Fade in={openForm} timeout={800}>
            <MDBox pt={3} pb={2}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Zoom in={openForm} timeout={600} style={{ transitionDelay: "100ms" }}>
                    <TextField
                      fullWidth
                      label="Plan Name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Enter plan name (e.g., 5 Post Pack)"
                      variant="outlined"
                      required
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "12px",
                          transition: "all 0.3s",
                          "&:hover": { transform: "translateX(5px)" },
                        },
                      }}
                    />
                  </Zoom>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Zoom in={openForm} timeout={600} style={{ transitionDelay: "150ms" }}>
                    <TextField
                      fullWidth
                      label="Post Count"
                      name="post_count"
                      type="number"
                      value={formData.post_count}
                      onChange={handleInputChange}
                      placeholder="Number of posts"
                      variant="outlined"
                      required
                      InputProps={{
                        startAdornment: <PostAddIcon sx={{ color: metaPurple, mr: 1 }} />,
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "12px",
                          transition: "all 0.3s",
                          "&:hover": { transform: "translateX(5px)" },
                        },
                      }}
                    />
                  </Zoom>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Zoom in={openForm} timeout={600} style={{ transitionDelay: "150ms" }}>
                    <TextField
                      fullWidth
                      label="View Count"
                      name="view_count"
                      type="number"
                      value={formData.view_count}
                      onChange={handleInputChange}
                      placeholder="Number of views"
                      variant="outlined"
                      required
                      InputProps={{
                        startAdornment: <VisibilityIcon sx={{ color: metaPurple, mr: 1 }} />,
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "12px",
                          transition: "all 0.3s",
                          "&:hover": { transform: "translateX(5px)" },
                        },
                      }}
                    />
                  </Zoom>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Zoom in={openForm} timeout={600} style={{ transitionDelay: "200ms" }}>
                    <TextField
                      fullWidth
                      label="Price (₹)"
                      name="price"
                      type="number"
                      value={formData.price}
                      onChange={handleInputChange}
                      placeholder="Enter price"
                      variant="outlined"
                      required
                      InputProps={{
                        startAdornment: <AttachMoneyIcon sx={{ color: metaPurple, mr: 1 }} />,
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "12px",
                          transition: "all 0.3s",
                          "&:hover": { transform: "translateX(5px)" },
                        },
                      }}
                    />
                  </Zoom>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Zoom in={openForm} timeout={600} style={{ transitionDelay: "250ms" }}>
                    <TextField
                      fullWidth
                      label="Sort Order"
                      name="sort_order"
                      type="number"
                      value={formData.sort_order}
                      onChange={handleInputChange}
                      placeholder="Enter sort order"
                      variant="outlined"
                      required
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "12px",
                          transition: "all 0.3s",
                          "&:hover": { transform: "translateX(5px)" },
                        },
                      }}
                    />
                  </Zoom>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Zoom in={openForm} timeout={600} style={{ transitionDelay: "300ms" }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.status === "active"}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              status: e.target.checked ? "active" : "inactive",
                            }))
                          }
                          sx={{
                            "& .MuiSwitch-switchBase.Mui-checked": { color: "#4caf50" },
                            "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                              backgroundColor: "#4caf50",
                            },
                          }}
                        />
                      }
                      label={
                        <MDTypography variant="button" fontWeight="medium">
                          {formData.status === "active" ? "Active" : "Inactive"}
                        </MDTypography>
                      }
                      sx={{ mt: 1 }}
                    />
                  </Zoom>
                </Grid>
              </Grid>
            </MDBox>
          </Fade>
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            pb: 3,
            pt: 2,
            gap: 2,
            justifyContent: "flex-end",
            position: "relative",
            zIndex: 1,
          }}
        >
          <Zoom in={openForm} timeout={600} style={{ transitionDelay: "350ms" }}>
            <MDButton
              variant="outlined"
              onClick={() => {
                setOpenForm(false);
                resetForm();
              }}
              disabled={isSubmitting}
              sx={{
                borderRadius: "12px",
                px: 3,
                py: 1,
                fontWeight: "bold",
                textTransform: "none",
                transition: "all 0.3s",
                borderColor: "#111",
                color: "#111",
                "&:hover": { transform: "translateY(-2px)", borderColor: "#111" },
              }}
            >
              Cancel
            </MDButton>
          </Zoom>

          <Zoom in={openForm} timeout={600} style={{ transitionDelay: "400ms" }}>
            <MDButton
              variant="gradient"
              color="dark"
              onClick={handleSubmit}
              disabled={isSubmitting}
              sx={{
                borderRadius: "12px",
                px: 4,
                py: 1,
                fontWeight: "bold",
                textTransform: "none",
                transition: "all 0.3s",
                "&:hover": { transform: "translateY(-2px)" },
                background: `linear-gradient(135deg, ${metaPurple} 0%, #7B1FA2 100%)`,
              }}
              startIcon={
                isSubmitting ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />
              }
            >
              {isSubmitting ? "PROCESSING..." : formMode === "add" ? "SAVE" : "UPDATE"}
            </MDButton>
          </Zoom>
        </DialogActions>
      </Dialog>

      {/* VIEW PLAN DIALOG */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        fullWidth
        maxWidth="sm"
        TransitionComponent={Transition}
        PaperProps={{
          sx: {
            borderRadius: "28px",
            background: "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
            boxShadow: "0 40px 80px rgba(0, 0, 0, 0.2)",
            position: "relative",
            overflow: "hidden",
          },
        }}
      >
        <IconButton
          onClick={() => setViewDialogOpen(false)}
          sx={{
            position: "absolute",
            right: 16,
            top: 16,
            zIndex: 10,
            bgcolor: "rgba(0,0,0,0.05)",
            transition: "all 0.3s ease",
            "&:hover": {
              transform: "rotate(90deg) scale(1.1)",
              bgcolor: `rgba(156, 39, 176, 0.1)`,
            },
          }}
        >
          <CloseIcon />
        </IconButton>

        <DialogTitle sx={{ pb: 1, pt: 3, textAlign: "center", position: "relative", zIndex: 1 }}>
          <Zoom in={viewDialogOpen} timeout={600}>
            <MDBox display="flex" flexDirection="column" alignItems="center" gap={2}>
              <MDBox
                sx={{
                  background: `linear-gradient(135deg, ${metaPurple} 0%, #CE93D8 100%)`,
                  width: 80,
                  height: 80,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: `0 8px 20px rgba(156, 39, 176, 0.3)`,
                  animation: `${float} 2s ease-in-out infinite`,
                }}
              >
                <ExtensionIcon sx={{ fontSize: 45, color: "#fff" }} />
              </MDBox>
              <MDTypography variant="h4" fontWeight="bold">
                {selectedPlan?.name}
              </MDTypography>
              <Chip
                label={selectedPlan?.status === "active" ? "ACTIVE" : "INACTIVE"}
                sx={{
                  bgcolor: selectedPlan?.status === "active" ? "#4caf50" : "#f44336",
                  color: "#fff",
                  fontWeight: "bold",
                }}
              />
            </MDBox>
          </Zoom>
        </DialogTitle>

        <DialogContent sx={{ position: "relative", zIndex: 1 }}>
          <Fade in={viewDialogOpen} timeout={800}>
            <Box sx={{ py: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Paper
                    sx={{
                      p: 3,
                      borderRadius: "20px",
                      background: "linear-gradient(135deg, #f3e5f5 0%, #f8f9fa 100%)",
                      textAlign: "center",
                    }}
                  >
                    <PostAddIcon
                      sx={{
                        fontSize: 40,
                        color: metaPurple,
                        mb: 1,
                        animation: `${rotateIn} 0.5s ease-out`,
                      }}
                    />
                    <MDTypography variant="caption" color="text" fontWeight="bold" display="block">
                      Total Posts
                    </MDTypography>
                    <MDTypography variant="h2" fontWeight="bold" color="primary">
                      {selectedPlan?.post_count}
                    </MDTypography>
                  </Paper>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Paper
                    sx={{
                      p: 3,
                      borderRadius: "20px",
                      background: "linear-gradient(135deg, #fff3e0 0%, #f8f9fa 100%)",
                      textAlign: "center",
                    }}
                  >
                    <AttachMoneyIcon
                      sx={{
                        fontSize: 40,
                        color: "#f44336",
                        mb: 1,
                        animation: `${rotateIn} 0.5s ease-out`,
                      }}
                    />
                    <MDTypography variant="caption" color="text" fontWeight="bold" display="block">
                      Price
                    </MDTypography>
                    <MDTypography variant="h2" fontWeight="bold" color="error">
                      ₹{selectedPlan?.price}
                    </MDTypography>
                  </Paper>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Paper sx={{ p: 2, borderRadius: "16px", bgcolor: "#f8f9fa" }}>
                    <MDTypography variant="caption" color="text" fontWeight="bold">
                      Sort Order
                    </MDTypography>
                    <MDTypography variant="h4" fontWeight="bold">
                      {selectedPlan?.sort_order}
                    </MDTypography>
                  </Paper>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Paper sx={{ p: 2, borderRadius: "16px", bgcolor: "#f8f9fa" }}>
                    <MDTypography variant="caption" color="text" fontWeight="bold">
                      Price per Post
                    </MDTypography>
                    <MDTypography variant="h4" fontWeight="bold" color="error">
                      ₹{(selectedPlan?.price / selectedPlan?.post_count).toFixed(2)}
                    </MDTypography>
                  </Paper>
                </Grid>

                {/* <Grid item xs={12}>
                  <Paper sx={{ p: 2, borderRadius: "16px", bgcolor: "#f8f9fa" }}>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <MDTypography variant="caption" color="text" fontWeight="bold">
                          Created At
                        </MDTypography>
                        <MDTypography variant="body2">
                          {formatDate(selectedPlan?.createdAt)}
                        </MDTypography>
                      </Grid>
                      <Grid item xs={6}>
                        <MDTypography variant="caption" color="text" fontWeight="bold">
                          Last Updated
                        </MDTypography>
                        <MDTypography variant="body2">
                          {formatDate(selectedPlan?.updatedAt)}
                        </MDTypography>
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid> */}
              </Grid>
            </Box>
          </Fade>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, justifyContent: "right" }}>
          <MDButton
            variant="gradient"
            onClick={() => setViewDialogOpen(false)}
            sx={{
              borderRadius: "12px",
              px: 4,
              background: `linear-gradient(135deg, ${metaPurple} 0%, #7B1FA2 100%)`,
              color: "#fff",
            }}
          >
            Close
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* PREMIUM DELETE CONFIRMATION DIALOG */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => !isSubmitting && setDeleteDialogOpen(false)}
        fullWidth
        TransitionComponent={Transition}
        PaperProps={{
          sx: {
            borderRadius: "32px",
            background: "linear-gradient(135deg, #ffffff 0%, #fef2f2 100%)",
            boxShadow: "0 40px 80px rgba(0, 0, 0, 0.2)",
            position: "relative",
            overflow: "hidden",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: "4px",
              background: "linear-gradient(90deg, #ef4444, #f87171, #ef4444)",
              animation: `${shimmer} 2s infinite linear`,
              backgroundSize: "200% 100%",
            },
          },
        }}
      >
        <IconButton
          onClick={() => !isSubmitting && setDeleteDialogOpen(false)}
          sx={{
            position: "absolute",
            right: 16,
            top: 16,
            zIndex: 10,
            bgcolor: "rgba(0,0,0,0.05)",
            transition: "all 0.3s ease",
            "&:hover": {
              transform: "rotate(90deg) scale(1.1)",
              bgcolor: "rgba(239, 68, 68, 0.1)",
            },
          }}
          disabled={isSubmitting}
        >
          <CloseIcon sx={{ color: "#ef4444" }} />
        </IconButton>

        <Box
          sx={{
            position: "absolute",
            top: -50,
            left: -50,
            right: -50,
            bottom: -50,
            background: "radial-gradient(circle, rgba(239,68,68,0.08) 0%, rgba(239,68,68,0) 70%)",
            pointerEvents: "none",
          }}
        />

        <DialogTitle sx={{ pt: 4, pb: 2, textAlign: "center", position: "relative", zIndex: 1 }}>
          <Grow in={deleteDialogOpen} timeout={600}>
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
              <Zoom in={deleteDialogOpen} timeout={600}>
                <Box
                  sx={{
                    position: "relative",
                    display: "inline-flex",
                    borderRadius: "50%",
                    animation: `${pulseRing} 2s infinite`,
                    cursor: "pointer",
                    mb: 2,
                    "&:hover": { animation: `${shakeAnimation} 0.5s ease-in-out` },
                  }}
                >
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: "50%",
                      background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      boxShadow: "0 8px 20px rgba(239, 68, 68, 0.4)",
                      animation: `${glow} 2s ease-in-out infinite`,
                    }}
                  >
                    <WarningAmberIcon sx={{ fontSize: 40, color: "#fff" }} />
                  </Box>
                </Box>
              </Zoom>

              <Box sx={{ animation: `${bounceIn} 0.6s cubic-bezier(0.68, -0.55, 0.265, 1.55)` }}>
                <Box
                  sx={{
                    background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
                    backgroundClip: "text",
                    WebkitBackgroundClip: "text",
                    color: "transparent",
                  }}
                >
                  <MDTypography variant="h4" fontWeight="bold">
                    Delete Extra Plan?
                  </MDTypography>
                </Box>
              </Box>
            </Box>
          </Grow>
        </DialogTitle>

        <DialogContent sx={{ position: "relative", zIndex: 1 }}>
          <Fade in={deleteDialogOpen} timeout={800}>
            <Box sx={{ textAlign: "center", py: 2 }}>
              <Zoom in={deleteDialogOpen} timeout={600} style={{ transitionDelay: "100ms" }}>
                <Alert
                  severity="warning"
                  icon={<WarningAmberIcon />}
                  sx={{
                    borderRadius: "16px",
                    mb: 3,
                    backgroundColor: "rgba(239, 68, 68, 0.1)",
                    border: "1px solid rgba(239, 68, 68, 0.2)",
                  }}
                >
                  <MDTypography variant="body2" fontWeight="medium" color="error">
                    This action cannot be undone!
                  </MDTypography>
                </Alert>
              </Zoom>

              <Grow in={deleteDialogOpen} timeout={600} style={{ transitionDelay: "150ms" }}>
                <Paper
                  elevation={0}
                  sx={{
                    background:
                      "linear-gradient(135deg, rgba(239,68,68,0.05) 0%, rgba(239,68,68,0.02) 100%)",
                    borderRadius: "20px",
                    p: 3,
                    border: "1px solid rgba(239, 68, 68, 0.2)",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "scale(1.02)",
                      borderColor: "rgba(239, 68, 68, 0.4)",
                    },
                  }}
                >
                  <MDTypography
                    variant="body2"
                    color="textSecondary"
                    gutterBottom
                    fontWeight="bold"
                  >
                    EXTRA PLAN TO BE DELETED
                  </MDTypography>
                  <MDTypography
                    variant="h6"
                    fontWeight="bold"
                    sx={{ color: "#ef4444", wordBreak: "break-word" }}
                  >
                    &ldquo;{deletePlanName}&ldquo;
                  </MDTypography>
                </Paper>
              </Grow>

              <Fade in={deleteDialogOpen} timeout={800} style={{ transitionDelay: "200ms" }}>
                <Box sx={{ mt: 3 }}>
                  <MDTypography
                    variant="body2"
                    color="textSecondary"
                    sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}
                  >
                    <WarningAmberIcon sx={{ fontSize: 16, color: "#ef4444" }} />
                    This extra plan will be permanently removed from the system
                  </MDTypography>
                </Box>
              </Fade>
            </Box>
          </Fade>
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            pb: 4,
            pt: 2,
            gap: 2,
            justifyContent: "center",
            position: "relative",
            zIndex: 1,
          }}
        >
          <Zoom in={deleteDialogOpen} timeout={600} style={{ transitionDelay: "250ms" }}>
            <MDButton
              variant="outlined"
              onClick={() => setDeleteDialogOpen(false)}
              disabled={isSubmitting}
              fullWidth
              sx={{
                borderRadius: "14px",
                py: 1.5,
                fontWeight: "bold",
                textTransform: "none",
                transition: "all 0.3s",
                borderColor: "#111",
                color: "#111",
                "&:hover": { transform: "translateY(-2px)", borderColor: "#111" },
              }}
            >
              Cancel
            </MDButton>
          </Zoom>

          <Zoom in={deleteDialogOpen} timeout={600} style={{ transitionDelay: "300ms" }}>
            <MDButton
              variant="contained"
              color="error"
              onClick={handleDeletePlan}
              disabled={isSubmitting}
              fullWidth
              sx={{
                borderRadius: "14px",
                py: 1.5,
                fontWeight: "bold",
                textTransform: "none",
                transition: "all 0.3s",
                "&:hover": { transform: "translateY(-2px)" },
              }}
              startIcon={
                isSubmitting ? <CircularProgress size={20} color="inherit" /> : <DeleteIcon />
              }
            >
              {isSubmitting ? "DELETING..." : "DELETE PERMANENTLY"}
            </MDButton>
          </Zoom>
        </DialogActions>

        <Box
          sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            height: "3px",
            background: "linear-gradient(90deg, #ef4444, #f87171, #ef4444)",
            animation: `${shimmer} 2s infinite linear`,
            backgroundSize: "200% 100%",
          }}
        />
      </Dialog>

      {/* SNACKBAR */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        TransitionComponent={Slide}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%", borderRadius: "12px" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
}

export default ExtraPlanManagement;
